import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Copy, ExternalLink, MessageCircle, Send, Share2 } from 'lucide-react';

// Sender name persisted to localStorage so outgoing shares can read
// "Lalit shared a wine country itinerary with you" instead of being
// anonymous. Lives at module scope so both popover and parent components
// can read/write through the same key.
const SENDER_NAME_KEY = 'winery-finder-sender-name';
export const getSenderName = () => {
  try { return (localStorage.getItem(SENDER_NAME_KEY) || '').trim(); }
  catch { return ''; }
};
export const setSenderName = (name) => {
  try {
    const v = (name || '').trim().slice(0, 40);
    if (v) localStorage.setItem(SENDER_NAME_KEY, v);
    else localStorage.removeItem(SENDER_NAME_KEY);
  } catch { /* storage unavailable — share just stays anonymous */ }
};

// Compose the personalised invite line. Every outgoing share is prefixed
// with the app attribution ("Shared from Winery Finder App – …") so the
// recipient immediately knows what they're looking at, even when their
// client doesn't unfurl the OG card (Slack DMs, basic SMS clients,
// stripped-down corporate mail filters, etc.). We avoid a hard-coded
// receiver name — the OS share sheet / email client already surfaces the
// recipient picker.
export const buildShareText = ({ sender, query }) => {
  const trip = (query || '').replace(/^['"]+|['"]+$/g, '').trim() || 'wine country trip plan';
  const named = (sender || '').trim();
  const intro = named
    ? `${named} shared a wine country trip plan with you: "${trip}"`
    : `Wine country trip plan: "${trip}"`;
  return `Shared from Winery Finder App – ${intro}`;
};

export const copyToClipboard = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    try { await navigator.clipboard.writeText(text); return true; } catch { /* fall through */ }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    ta.style.pointerEvents = 'none';
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
};

const openShare = (url) => {
  try { return Boolean(window.open(url, '_blank', 'noopener,noreferrer')); }
  catch { return false; }
};

const SHARE_TARGETS = [
  { id: 'whatsapp', label: 'WhatsApp', iconColor: 'text-[#25D366]',
    build: ({ url, text }) => `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}` },
  { id: 'x', label: 'X / Twitter', iconColor: 'text-[#1d9bf0]',
    build: ({ url, text }) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}` },
  { id: 'facebook', label: 'Facebook', iconColor: 'text-[#1877F2]',
    build: ({ url }) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
  { id: 'sms', label: 'Messages (SMS)', iconColor: 'text-[#34c759]',
    build: ({ url, text }) => `sms:?&body=${encodeURIComponent(`${text} ${url}`)}` },
  { id: 'mailto', label: 'Open in mail app', iconColor: 'text-[#722f37]',
    build: ({ url, text }) => `mailto:?subject=${encodeURIComponent((text || '').slice(0, 90))}&body=${encodeURIComponent(`${text}\n\n${url}`)}` },
];

/**
 * SharePopover — single-button share UI for any saved /trip/{slug}.
 *
 * v3: no inline email widget (consolidated into Save & Share). Adds sender
 * personalisation: a tiny edit-in-place line at the top lets the user set
 * their name once (persisted via localStorage), which then flows into the
 * outgoing share text as "[Sender] shared a wine country itinerary with
 * you: '[query]'". Empty sender → falls back to a generic line.
 */
const SharePopover = ({ open, onClose, slug, title, shareUrl, className = '' }) => {
  const ref = useRef(null);
  const [sender, setSender] = useState(() => getSenderName());
  const [editingSender, setEditingSender] = useState(false);
  const [senderDraft, setSenderDraft] = useState('');
  const [placeAbove, setPlaceAbove] = useState(false);

  useEffect(() => {
    if (!open) {
      setEditingSender(false);
      setSenderDraft('');
      return undefined;
    }
    setSender(getSenderName());
    const measure = () => {
      const triggerEl = ref.current?.previousElementSibling;
      if (!triggerEl) return;
      const rect = triggerEl.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const POPOVER_HEIGHT = 330;
      setPlaceAbove(spaceBelow < POPOVER_HEIGHT && spaceAbove > spaceBelow);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open, onClose]);

  if (!open || !slug) return null;

  const shareText = buildShareText({ sender, query: title });
  const positionClass = placeAbove ? 'bottom-full mb-2' : 'top-full mt-2';
  const supportsNativeShare = (() => {
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
    try {
      if (typeof navigator.canShare === 'function') return navigator.canShare({ url: shareUrl, text: shareText });
      return true;
    } catch { return false; }
  })();

  const handleCopy = async () => {
    const ok = await copyToClipboard(`${shareText}\n${shareUrl}`);
    if (ok) toast.success('Link copied to clipboard');
    else toast.error('Copy failed — please long-press the link to copy manually.');
  };

  const handleSocial = (target) => {
    const ok = openShare(target.build({ url: shareUrl, text: shareText }));
    if (!ok) toast.error('Pop-up blocked — please allow pop-ups for this site.');
    else onClose?.();
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title: shareText, text: shareText, url: shareUrl });
      onClose?.();
    } catch (err) {
      if (err?.name === 'AbortError') return;
      const ok = await copyToClipboard(`${shareText}\n${shareUrl}`);
      if (ok) toast.success('Native share unavailable here — link copied instead.');
      else toast.error('Sharing isn\u2019t supported in this view. Long-press the URL to copy.');
    }
  };

  const handleSaveSender = () => {
    const v = senderDraft.trim();
    setSenderName(v);
    setSender(v);
    setEditingSender(false);
  };

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Share this trip"
      className={`absolute left-0 ${positionClass} w-[320px] max-h-[80vh] overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-200 p-3 z-40 ${className}`}
      data-testid="share-popover"
    >
      <div className="flex items-center justify-between gap-2 mb-2 px-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400">
          Share this trip
        </div>
        {!editingSender ? (
          <button
            type="button"
            onClick={() => { setSenderDraft(sender); setEditingSender(true); }}
            className="text-[10px] font-semibold text-wine-700 hover:text-wine-900 underline decoration-dotted"
            data-testid="share-sender-edit"
            title="Personalise the share message with your name"
          >
            {sender ? `Sharing as: ${sender}` : '+ Add your name'}
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <input
              type="text"
              autoFocus
              maxLength={40}
              placeholder="Your name"
              value={senderDraft}
              onChange={(e) => setSenderDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveSender();
                else if (e.key === 'Escape') setEditingSender(false);
              }}
              className="text-[11px] px-1.5 py-1 rounded border border-gray-300 focus:border-wine-500 focus:ring-1 focus:ring-wine-500 outline-none w-[100px]"
              data-testid="share-sender-input"
            />
            <button
              type="button"
              onClick={handleSaveSender}
              className="text-[10px] font-semibold text-white bg-wine-600 hover:bg-wine-700 px-1.5 py-1 rounded"
              data-testid="share-sender-save"
            >
              Save
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-5 gap-1.5 mb-2">
        {SHARE_TARGETS.map((target) => (
          <button
            key={target.id}
            type="button"
            onClick={() => handleSocial(target)}
            className="group flex flex-col items-center gap-1 px-1 py-2 rounded-lg hover:bg-wine-50 transition-colors"
            aria-label={`Share to ${target.label}`}
            title={target.label}
            data-testid={`share-${target.id}`}
          >
            <span className={`w-8 h-8 rounded-full bg-gray-50 group-hover:bg-white border border-gray-200 flex items-center justify-center ${target.iconColor} transition-colors`}>
              <SocialGlyph id={target.id} />
            </span>
            <span className="text-[9px] font-medium text-gray-500 leading-tight text-center truncate w-full">
              {target.label}
            </span>
          </button>
        ))}
      </div>

      <div className="border-t border-gray-100 pt-2">
        <div className="grid grid-cols-1 gap-1">
          <button
            type="button"
            onClick={handleCopy}
            className="flex items-center gap-2 text-xs font-medium text-[#1a1a2e] hover:bg-wine-50 rounded-md px-2.5 py-2 transition-colors text-left"
            data-testid="share-copy-link"
          >
            <Copy className="w-3.5 h-3.5 text-wine-600" />
            Copy link
          </button>

          <Link
            to={`/trip/${slug}`}
            onClick={() => onClose?.()}
            className="flex items-center gap-2 text-xs font-medium text-[#1a1a2e] hover:bg-wine-50 rounded-md px-2.5 py-2 transition-colors"
            data-testid="share-open-trip"
          >
            <ExternalLink className="w-3.5 h-3.5 text-wine-600" />
            Open trip page
          </Link>

          {supportsNativeShare && (
            <button
              type="button"
              onClick={handleNativeShare}
              className="flex items-center gap-2 text-xs font-medium text-[#1a1a2e] hover:bg-wine-50 rounded-md px-2.5 py-2 transition-colors text-left"
              data-testid="share-native"
            >
              <Share2 className="w-3.5 h-3.5 text-wine-600" />
              Share via system sheet…
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const SocialGlyph = ({ id }) => {
  if (id === 'whatsapp') {
    return (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
        <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.599 5.366l-.999 3.648 3.889-1.013zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413z"/>
      </svg>
    );
  }
  if (id === 'x') {
    return (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    );
  }
  if (id === 'facebook') {
    return (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    );
  }
  if (id === 'sms') return <MessageCircle className="w-4 h-4" />;
  if (id === 'mailto') return <Send className="w-4 h-4" />;
  return null;
};

export default SharePopover;
